import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Bot, Send, Loader2, Sparkles, User, Plus } from "lucide-react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

function renderText(text) {
  return text.split("\n").map((line, i) => {
    const bold = line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (/^#{1,3}\s/.test(line))
      return <div key={i} className="mt-2 font-heading text-sm font-bold" dangerouslySetInnerHTML={{ __html: bold.replace(/^#{1,3}\s/, "") }} />;
    if (/^[-*]\s/.test(line))
      return <div key={i} className="ml-3 flex gap-2"><span className="text-[hsl(var(--accent))]">•</span><span dangerouslySetInnerHTML={{ __html: bold.replace(/^[-*]\s/, "") }} /></div>;
    if (!line.trim()) return <div key={i} className="h-2" />;
    return <div key={i} dangerouslySetInnerHTML={{ __html: bold }} />;
  });
}

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [cid, setCid] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const endRef = useRef();

  useEffect(() => { api.get("/ai/prompts").then((r) => setPrompts(r.data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { conversation_id: cid, message: msg });
      setCid(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error("The AI assistant is unavailable right now.");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))] text-white"><Bot className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">AI Compliance Assistant</h1>
            <p className="text-xs text-muted-foreground">Your virtual Compliance Manager · Claude Sonnet 4.6</p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" className="rounded-full" onClick={() => { setMessages([]); setCid(null); }} data-testid="new-chat-btn">
            <Plus className="mr-2 h-4 w-4" /> New chat
          </Button>
        )}
      </div>

      <Card className="flex flex-1 flex-col overflow-hidden rounded-2xl border-border">
        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <Sparkles className="h-8 w-8 text-[hsl(var(--accent))]" />
              <h3 className="mt-4 font-heading text-lg font-bold">How can I help with compliance today?</h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">Ask me to draft toolbox talks, investigation reports, risk assessments, corrective actions or board summaries.</p>
              <div className="mt-6 grid w-full max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                {prompts.map((p) => (
                  <button key={p.label} onClick={() => send(p.prompt)} data-testid={`prompt-${p.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="rounded-xl border border-border bg-card p-3 text-left text-sm transition-all hover:-translate-y-0.5 hover:border-[hsl(var(--accent))] hover:shadow-sm">
                    <div className="font-medium">{p.label}</div>
                    <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{p.prompt}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-secondary" : "bg-[hsl(var(--accent))] text-white"}`}>
                {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[hsl(var(--accent))] text-white" : "bg-secondary text-foreground"}`}>
                {m.role === "assistant" ? <div className="space-y-0.5">{renderText(m.content)}</div> : m.content}
              </div>
            </motion.div>
          ))}
          {busy && (
            <div className="flex gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-white"><Bot className="h-4 w-4" /></div>
              <div className="flex items-center gap-1 rounded-2xl bg-secondary px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-border p-3">
          <div className="flex items-end gap-2">
            <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask the compliance assistant…" className="min-h-[44px] resize-none rounded-xl" data-testid="ai-input" />
            <Button onClick={() => send()} disabled={busy || !input.trim()} className="h-11 w-11 shrink-0 rounded-xl p-0" data-testid="ai-send-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
